import { NavLink } from 'react-router-dom';
import { useState, useEffect, useCallback, type ReactNode } from 'react';
import CaptureModal from './CaptureModal';
import { StellaPanel } from './StellaPanel';
import { currentUser, signOut } from '../authHeaders';
import '../styles/stella.css';

const navItems = [
  { path: '/inbox', label: 'Inbox', icon: 'mail', section: 'notifications' },
  { path: '/teams', label: 'Teams', icon: 'users', section: 'notifications' },
  { path: '/calendar', label: 'Calendar', icon: 'calendar', section: 'notifications' },
  { path: '/org-chart', label: 'Org Chart', icon: 'org-chart', section: 'notifications' },
  { path: '/mim', label: 'Major Incident Cmd', icon: 'alert-circle', section: 'alerts' },
  { path: '/domain-health', label: 'Domain Health', icon: 'heart', section: 'alerts' },
  { path: '/outage-pipeline', label: 'Outage Pipeline', icon: 'zap', section: 'outage' },
  { path: '/', label: 'Overview', icon: 'home', section: 'core' },
  { path: '/registry', label: 'CI Registry', icon: 'list', section: 'core' },
  { path: '/reconciliation', label: 'Drift & Conflict', icon: 'refresh', section: 'core' },
  { path: '/ingest-queue', label: 'Ingest Queue', icon: 'upload', section: 'core' },
  { path: '/workspace', label: 'My Workspace', icon: 'layout', section: 'core' },
  { path: '/toil', label: 'Toil & Tickets', icon: 'ticket', section: 'operations' },
  { path: '/servicenow-analytics', label: 'SN Ticket Analytics', icon: 'bar-chart', section: 'operations' },
  { path: '/kpi-analytics', label: 'KPI Analytics', icon: 'target', section: 'operations' },
  { path: '/taskers', label: 'Taskers', icon: 'check-square', section: 'operations' },
  { path: '/pacing', label: 'Migration Pacing', icon: 'activity', section: 'operations' },
  { path: '/campaigns', label: 'Campaign Portfolio', icon: 'folder', section: 'operations' },
  { path: '/aws', label: 'AWS Cloud', icon: 'cloud', section: 'operations' },
  { path: '/settings', label: 'Settings', icon: 'settings', section: 'bottom' },
];

const STORAGE_KEY = 'atlas_sidebar_collapsed';

interface AppShellProps {
  children: ReactNode;
}

// Simple SVG icon component
function Icon({ name, size = 16 }: { name: string; size?: number }) {
  const icons: Record<string, JSX.Element> = {
    home: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></>,
    list: <><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></>,
    refresh: <><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
    zap: <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></>,
    monitor: <><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><path d="M8 21h8M12 17v4" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
    dollar: <><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>,
    'trending-down': <><polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" /></>,
    map: <><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></>,
    ticket: <><path d="M15 5H9a2 2 0 0 0-2 2v2a2 2 0 0 1 2 2v6a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-6a2 2 0 0 1 2-2V7a2 2 0 0 0-2-2z" /><path d="M9 12h6" /></>,
    'bar-chart': <><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></>,
    activity: <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></>,
    folder: <><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></>,
    'alert-circle': <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
    'help-circle': <><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
    'alert-triangle': <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
    heart: <><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></>,
    target: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>,
    trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
    cloud: <><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" /></>,
    cpu: <><rect x="4" y="4" width="16" height="16" rx="2" ry="2" /><rect x="9" y="9" width="6" height="6" /><line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" /><line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" /><line x1="20" y1="9" x2="23" y2="9" /><line x1="20" y1="14" x2="23" y2="14" /><line x1="1" y1="9" x2="4" y2="9" /><line x1="1" y1="14" x2="4" y2="14" /></>,
    users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
    mail: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>,
    layout: <><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></>,
    upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
    'org-chart': <><circle cx="12" cy="5" r="2" /><circle cx="5" cy="19" r="2" /><circle cx="19" cy="19" r="2" /><path d="M12 7v4M12 11l-7 6M12 11l7 6" /></>,
    'check-square': <><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
    rss: <><path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 4a16 16 0 0 1 16 16" /><circle cx="5" cy="19" r="1" /></>,
  };

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {icons[name] || icons.home}
    </svg>
  );
}

export function AppShell({ children }: AppShellProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [stellaOpen, setStellaOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(collapsed)); } catch { /* noop */ }
  }, [collapsed]);

  useEffect(() => { setMobileOpen(false); }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'u') {
        e.preventDefault();
        setShowCaptureModal(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const toggleMobile = useCallback(() => setMobileOpen(o => !o), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const notificationsItems = navItems.filter(n => n.section === 'notifications');
  const alertsItems = navItems.filter(n => n.section === 'alerts');
  const outageItems = navItems.filter(n => n.section === 'outage');
  const coreItems = navItems.filter(n => n.section === 'core');
  const opsItems = navItems.filter(n => n.section === 'operations');
  const bottomItems = navItems.filter(n => n.section === 'bottom');

  const renderNavSection = (title: string | null, items: typeof navItems) => (
    <>
      {title && <h3 className="side__group">{title}</h3>}
      {items.map(item => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === '/'}
          className="nav"
          onClick={closeMobile}
          title={collapsed ? item.label : undefined}
        >
          {({ isActive }) => (
            <>
              <Icon name={item.icon} />
              <span className="nav__label">{item.label}</span>
              {isActive && <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 6, bottom: 6, width: 2, borderRadius: '0 2px 2px 0', background: 'var(--gold)' }} />}
            </>
          )}
        </NavLink>
      ))}
    </>
  );

  return (
    <>
      {/* Classification Banner - Top */}
      <div className="banner">
        CUI//SP-CTI — <b>ATLAS</b> — IT Operations Accountability Platform
      </div>

      <div className="app">
        {/* Sidebar */}
        <aside className={`side ${collapsed ? 'side--mini' : ''} ${mobileOpen ? 'open' : ''}`}>
          <div className="side__brand">
            <div className="side__mark">A</div>
            {!collapsed && (
              <div className="side__name">
                <b>ATLAS</b>
                <span>ITOps Platform</span>
              </div>
            )}
          </div>

          <nav className="side__nav" aria-label="Main navigation">
            {renderNavSection('Notifications', notificationsItems)}
            {renderNavSection('Alerts', alertsItems)}
            {renderNavSection('Outage', outageItems)}
            {renderNavSection('Core', coreItems)}
            {renderNavSection('Operations', opsItems)}
            {renderNavSection(null, bottomItems)}
          </nav>

          <div className="side__user">
            <div className="side__avatar">{currentUser()?.initials ?? 'AS'}</div>
            {!collapsed && (
              <div className="side__user-txt">
                <b>{currentUser()?.username ?? 'Admin User'}</b>
                <span>IT Operations</span>
              </div>
            )}
            {!collapsed && currentUser() && (
              <button className="ibtn" onClick={signOut} aria-label="Sign out" title="Sign out">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <div className="main">
          {/* Topbar */}
          <div className="topbar">
            <button
              className="ibtn"
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>

            <div className="crumb">
              <a href="/">Home</a>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
              <b>Dashboard</b>
            </div>

            <div className="topbar__spacer" />

            <div className="search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input type="search" placeholder="Search..." aria-label="Search" />
            </div>

            <div className="ingest-upload-group">
              <button className="nav-upload-btn" onClick={() => setShowCaptureModal(true)} title="Upload documents (⌘U)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Upload
              </button>
              <span className="ingest-upload-kbd">⌘U</span>
            </div>
          </div>

          {/* Scroll Region */}
          <div className="scroll">
            <div className="page">
              <div id="main-content">
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Classification Banner - Bottom */}
      <div className="banner banner--bottom">
        CUI//SP-CTI — <b>ATLAS</b> — IT Operations Accountability Platform
      </div>

      {mobileOpen && <div className="scrim is-on" onClick={() => setMobileOpen(false)} />}

      {showCaptureModal && <CaptureModal onClose={() => setShowCaptureModal(false)} />}

      <StellaPanel open={stellaOpen} onClose={() => setStellaOpen(false)} />
      <button
        className={`stella-launcher${stellaOpen ? ' hidden' : ''}`}
        id="stella-launcher"
        onClick={() => setStellaOpen((v) => !v)}
        title="Open Stella Chat"
        aria-label="Open Stella Chat"
      >
        <span className="stella-launcher-orb" />
        <span className="stella-launcher-label">Ask Stella</span>
      </button>
    </>
  );
}
